import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useFamilyTree } from '../../hooks/useFamilyTree'
import { useUserSettings } from '../../hooks/useUserSettings'
import TreeCanvas from './TreeCanvas'
import TreeControlPanel from './TreeControlPanel'
import TreeInfoPane from './TreeInfoPane'

export default function TreePage({ user, familyId }) {
  const { text, people, loading } = useFamilyTree(user, familyId)
  const { defaultTreeRoot } = useUserSettings(user)
  const [rootName, setRootName] = useState(null)
  const [manualRoot, setManualRoot] = useState(null)
  const [stats, setStats] = useState(null)
  const [hoverInfo, setHoverInfo] = useState(null)
  const canvasRef = useRef(null)

  // Set default root when people load
  useEffect(() => {
    if (people && people.length > 0 && !manualRoot) {
      if (defaultTreeRoot) {
        const rootPerson = people.find(person => person.id === defaultTreeRoot)
        if (rootPerson) {
          setManualRoot(rootPerson.name)
          return
        }
      }

      const personWithMostConnections = people.reduce((best, person) => {
        const connections = (person.spouses?.length || 0) + (person.children?.length || 0) + (person.parents?.length || 0)
        const bestConnections = (best.spouses?.length || 0) + (best.children?.length || 0) + (best.parents?.length || 0)
        return connections > bestConnections ? person : best
      }, people[0])

      setManualRoot(personWithMostConnections.name)
    }
  }, [people, manualRoot, defaultTreeRoot])

  const handleRootChange = useCallback((name) => {
    setRootName(name)
  }, [])

  const handleTreeStats = useCallback((s) => {
    setStats(s)
  }, [])

  const handleInfoHover = useCallback((info) => {
    setHoverInfo(info)
  }, [])

  const handleDetailChange = useCallback((value) => {
    if (canvasRef.current) {
      canvasRef.current.setDetailLevel(value)
    }
  }, [])

  const handleRootSelect = useCallback((person) => {
    if (person) {
      setManualRoot(person.name)
    }
  }, [])

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-[calc(100vh-64px)] gap-4">
        <div className="glass-card p-8 flex flex-col items-center gap-4">
          <svg className="animate-spin h-12 w-12 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">Loading family data...</p>
        </div>
      </div>
    )
  }

  if (!text) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="glass-card p-8 max-w-md text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">No Family Data Yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Start building your kutumb by adding people and families.
          </p>
          <Link to="/admin" className="glass-btn-primary inline-block">
            Get Started
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <TreeControlPanel
        rootName={rootName}
        stats={stats}
        people={people}
        onDetailChange={handleDetailChange}
        onRootSelect={handleRootSelect}
      />
      <div className="flex-1 overflow-auto">
        <TreeCanvas
          ref={canvasRef}
          text={text}
          manualRoot={manualRoot}
          onRootChange={handleRootChange}
          onTreeStats={handleTreeStats}
          onInfoHover={handleInfoHover}
        />
      </div>
      <TreeInfoPane info={hoverInfo} />
    </div>
  )
}
